import React, { useEffect, useState, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Bouton ⬇️ scroll-to-bottom réutilisable. S57 (5 mai 2026).
 *
 * Positionnement : `absolute` (le parent doit avoir `position: relative`),
 * hors du flux du conteneur de scroll → pas d'oscillation distFromBottom
 * lors de l'apparition/disparition.
 *
 * Hystérésis : apparaît à `thresholdShow` (défaut 200 px) du bas, disparaît
 * à `thresholdHide` (défaut 80 px). Empêche le flip-flop quand le contenu
 * pousse autour du seuil pendant un stream.
 *
 * Couleur neutre adaptative jour/nuit (cf. fix S56 F polish).
 *
 * Usage :
 *   const containerRef = useRef(null);
 *   <div ref={containerRef} style={{ position: 'relative', overflowY: 'auto' }}>
 *     {messages...}
 *     <ScrollToBottomButton containerRef={containerRef} />
 *   </div>
 */
const ScrollToBottomButton = ({
    containerRef,
    thresholdShow = 200,
    thresholdHide = 80,
    bottom = 20,
}) => {
    const { isDark } = useTheme();
    const [show, setShow] = useState(false);

    useEffect(() => {
        const el = containerRef?.current;
        if (!el) return;
        const handler = () => {
            const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
            // Hystérésis : apparaît à thresholdShow, disparaît à thresholdHide
            setShow(prev => prev ? dist > thresholdHide : dist > thresholdShow);
        };
        el.addEventListener('scroll', handler, { passive: true });
        // Évalue au mount (cas où le contenu est déjà long)
        handler();
        return () => el.removeEventListener('scroll', handler);
    }, [containerRef, thresholdShow, thresholdHide]);

    const onClick = useCallback(() => {
        const el = containerRef?.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [containerRef]);

    if (!show) return null;

    return (
        <button
            onClick={onClick}
            title="Aller en bas"
            style={{
                position: 'absolute',
                bottom: `${bottom}px`, left: '50%', transform: 'translateX(-50%)',
                width: '34px', height: '34px', borderRadius: '50%',
                background: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.85)',
                border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.10)',
                cursor: 'pointer',
                color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 10px rgba(0,0,0,0.08)',
                backdropFilter: 'blur(6px)',
                animation: 'fadeIn 0.2s ease', zIndex: 10,
            }}
        >
            <ArrowDown size={14} />
        </button>
    );
};

export default ScrollToBottomButton;
